import { AxiosError } from 'axios';
import { omit } from 'lodash';

import { ServiceRequestConfig } from '~/services/createService';
import { ColumnSortOrder } from '~/types/Columns';

interface Identifiable {
  id: any;
}

type PayloadWithQueryOptions<T> = T extends object
  ? T & { rtkQueryOptions?: ServiceRequestConfig }
  : T | { payload: T; rtkQueryOptions?: ServiceRequestConfig };

export type AsyncMethodWithOptions<QueryArg, ResultType> = (
  arg: QueryArg,
  options?: ServiceRequestConfig,
) => Promise<ResultType>;

export const extractQueryOptions = <T>(arg: PayloadWithQueryOptions<T>) => {
  if (typeof arg !== 'object' || arg === null || !('rtkQueryOptions' in arg)) {
    return { payload: arg as T, options: {} };
  }

  if ('payload' in arg) {
    return { payload: arg.payload as T, options: arg.rtkQueryOptions ?? {} };
  }

  return {
    payload: omit(arg, 'rtkQueryOptions') as T,
    options: arg.rtkQueryOptions ?? {},
  };
};

/**
 * Higher order function that wraps an async method and allows passing rtkQueryOptions alongside the payload.
 *
 * Supports three patterns:
 * 1. Primitive as-is: `getGroup("123")`
 * 2. Wrapped primitive: `getGroup({ payload: "123", rtkQueryOptions: { showErrorToast: true } })`
 * 3. Object with merged options: `batchAddNodes({ items: [...], rtkQueryOptions: { showErrorToast: true } })`
 */
export const withOptions = <QueryArg, ResultType>(
  fn: AsyncMethodWithOptions<QueryArg, ResultType>,
) => {
  return (arg: PayloadWithQueryOptions<QueryArg>) => {
    const { payload, options } = extractQueryOptions(arg);
    return fn(payload, options);
  };
};

export const createQueryFn =
  <ResultType, QueryArg>(asyncMethod: (arg: QueryArg) => Promise<ResultType>) =>
  async (arg: QueryArg): Promise<{ data?: ResultType; error: any }> => {
    let data, error: any;

    try {
      data = await asyncMethod(arg);
    } catch (e) {
      error = e;
    }

    return { data, error };
  };

export const toSortParam = (sortBy: ColumnSortOrder): string => {
  return `${sortBy.desc ? '-' : ''}${sortBy.id}`;
};

export const testIsAxiosError = <E>(error: unknown): error is AxiosError<E> =>
  (error as AxiosError<E>).isAxiosError;

export function patchById<T extends Identifiable>(
  list: T[] | undefined,
  id: any,
  patch: T | undefined,
) {
  const item = list?.find((key) => key.id === id);

  if (item) {
    Object.assign(item, patch);
  }
}

export function filterById<T extends Identifiable>(
  list: T[] | undefined,
  id: any,
) {
  return list?.filter((item) => item.id !== id);
}

// TODO EKP-27750: this util exists to coerce RTKQ errors to the `any` type (as legacy code is consuming errors as `any`).  It should be deprecated once all API errors are transformed to RTKQ error interfaces (i.e. FetchBaseQueryError | SerializedError)
export const coerceRtkqError = (rtkqError: unknown): any => {
  return rtkqError as any;
};
